import { useSession } from "@platform/ui/features";
import { useEffect, useState } from "react";
import { targetState } from "./targets";

/** What waits in each queue the operator holds; undefined while loading or for a queue they do not hold. */
export interface QueueCounts {
  readonly verification?: number;
  /** Cases past the 24-hour decision target (card 117). */
  readonly verificationOverdue?: number;
  readonly productReviews?: number;
  readonly productReviewsOverdue?: number;
  readonly riskMatches?: number;
  readonly disputes?: number;
}

/**
 * The work queues (ADM-2, ADM-3): verification awaiting review, open product reviews, published products new
 * risk-list entries matched (card 076), and open disputes (card 090), for the operators who hold them. The Queues
 * page and the sidebar's counts read the same (card 110).
 */
export function useQueueCounts(): QueueCounts {
  const { session, state } = useSession();
  const permissions = state.status === "signed_in" ? state.claims.perm : [];
  const verifies = permissions.includes("ops:verification.review");
  const reviews = permissions.includes("ops:catalog.manage");
  const risks = permissions.includes("ops:risk.manage");
  const resolves = permissions.includes("ops:dispute.resolve");
  const [counts, setCounts] = useState<QueueCounts>({});

  useEffect(() => {
    let cancelled = false;
    const set = (key: keyof QueueCounts) => (count: number | undefined) => {
      if (!cancelled && count !== undefined) setCounts((current) => ({ ...current, [key]: count }));
    };
    if (verifies)
      void session.api
        .GET("/v1/ops/verification-cases", { params: { query: { state: "awaiting_review" } } })
        .then(({ data }) => {
          set("verification")(data?.items.length);
          set("verificationOverdue")(
            data?.items.filter((item) => targetState(item.reviewStartedAt).kind === "overdue").length,
          );
        });
    if (reviews)
      void session.api
        .GET("/v1/ops/product-reviews", { params: { query: { state: "open" } } })
        .then(({ data }) => {
          set("productReviews")(data?.items.length);
          set("productReviewsOverdue")(
            data?.items.filter((item) => targetState(item.createdAt).kind === "overdue").length,
          );
        });
    if (risks)
      void session.api
        .GET("/v1/ops/risk-matches", { params: { query: { state: "open" } } })
        .then(({ data }) => set("riskMatches")(data?.items.length));
    if (resolves)
      void session.api
        .GET("/v1/ops/disputes", { params: { query: { state: "open" } } })
        .then(({ data }) => set("disputes")(data?.items.length));
    return () => {
      cancelled = true;
    };
  }, [session, verifies, reviews, risks, resolves]);
  return counts;
}
